import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useMemo } from 'react'

import { Divider } from '@/components/dividers/Divider.tsx'
import { IterationDividerLabel } from '@/components/dividers/IterationDividerLabel.tsx'
import { RELEVANT_STORIES_CONTAINER } from '@/constants/CONTAINER_NAMES.ts'
import { useGroupRelevantStories } from '@/hooks/useGroupRelevantStories.tsx'
import { useQueryCurrentIteration, useQueryDeadlines, useQueryStories } from '@/requests/queries/useQueryProject.ts'
import { toReadableDate } from '@/utils/date.ts'

type Props = {
  filter?: (stories: Story[]) => Story[]
}

export const RelevantPanel = ({ filter }: Props) => {
  const { stories } = useQueryStories('relevant')
  const { setNodeRef } = useDroppable({ id: RELEVANT_STORIES_CONTAINER })
  const groupedStories = useGroupRelevantStories()
  const { iterationDeadlines } = useQueryDeadlines()
  const { currentIteration } = useQueryCurrentIteration()

  const renderList = useMemo(() => {
    if (filter) {
      return groupedStories.map((iterationWithStories) => ({
        ...iterationWithStories,
        stories: filter(iterationWithStories.stories),
      }))
    }

    return groupedStories
  }, [groupedStories, filter])

  return (
    <div className="flex-1 flex flex-col overflow-auto gap-2 bg-surface-backgroundElevated rounded-xl p-2 pr-0.5 min-w-[520px]">
      <div className="h-8 flex items-center gap-2">
        <span className="font-EN-KR-heading3-bold text-contents-primary">Current Iteration/Backlog</span>
        <div className="flex flex-row items-center py-4 px-3 gap-[1px]">
          <img src="/images/graph-icon.svg" alt="velocity" className="w-[14px] h-[14px]" />
          <span className="font-EN-KR-body2-regular text-contents-primary mx-0.5">
            {currentIteration?.velocity ?? 0}
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-scroll tracker-scrollbar">
        <SortableContext items={stories} strategy={verticalListSortingStrategy}>
          <ul className="overflow-hidden flex flex-col gap-1">
            {renderList.length === 1 && renderList[0].stories.length === 0 ? (
              <>
                <Divider uniqueKey={RELEVANT_STORIES_CONTAINER} stories={[]}>
                  <IterationDividerLabel {...renderList[0]} />
                </Divider>
                <li
                  ref={setNodeRef}
                  className="flex justify-center items-center h-[216px] gap-4 bg-surface-backgroundElevated"
                >
                  <p className="text-center font-EN-KR-body1-regular text-tb-grey-700">
                    Stories you are currently working on, <br /> and stories you’ve prioritized to work on next are
                    displayed here.
                  </p>
                </li>
              </>
            ) : (
              <>
                {renderList.map((iteration, index) => (
                  <Divider
                    key={iteration.number}
                    uniqueKey={iteration.number.toString()}
                    stories={iteration.stories}
                    isDraggableStories
                    isClosedByDefault={iteration.stories.length === 0}
                    footer={(
                      iterationDeadlines.find((release) => release.iterationNumber === iteration.number)?.deadlines ??
                      []
                    ).map(({ title, deadline }, index) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey:
                        key={index}
                        className="flex w-full px-1 py-2 justify-center align-middle gap-2 bg-surface-releaseMarker-predict text-contents-onDarkPrimary font-EN-KR-body2-bold"
                      >
                        {toReadableDate(deadline)}: deadline for {title}
                      </div>
                    ))}
                  >
                    <IterationDividerLabel {...iteration} type={index === 0 ? 'currentIteration' : 'generic'} />
                  </Divider>
                ))}

                <div ref={setNodeRef} className="flex justify-between gap-4 bg-transparent p-[0.3rem] h-4" />
              </>
            )}
          </ul>
        </SortableContext>
      </div>
    </div>
  )
}
